import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card } from './fields';
import { useToast } from '../../hooks/use-toast';
import { Save, Send, Loader2, Mail, KeyRound, ShieldCheck } from 'lucide-react';

const EmailSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [form, setForm] = useState({ enabled: false, apiKey: '', fromEmail: '', fromName: '', toEmail: '' });

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/email-settings').then((r) => {
      const d = r.data;
      setForm({ enabled: d.enabled, apiKey: '', fromEmail: d.fromEmail || '', fromName: d.fromName || '', toEmail: d.toEmail || '' });
      setHasApiKey(d.hasApiKey);
      setApiKeyMasked(d.apiKeyMasked || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { enabled: form.enabled, fromEmail: form.fromEmail, fromName: form.fromName, toEmail: form.toEmail };
      if (form.apiKey && form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      await api.put('/email-settings', payload);
      if (payload.apiKey) { setHasApiKey(true); setApiKeyMasked('****' + payload.apiKey.slice(-4)); }
      setForm((f) => ({ ...f, apiKey: '' }));
      toast({ title: 'Saved!', description: 'Email notification settings updated.' });
    } catch (e) {
      toast({ title: 'Save failed', description: 'Please try again.', variant: 'destructive' });
    }
    setSaving(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/email-settings/test');
      toast({ title: 'Test email sent', description: r.data.message });
    } catch (e) {
      toast({ title: 'Test failed', description: e.response?.data?.detail || 'Could not send test email.', variant: 'destructive' });
    }
    setTesting(false);
  };

  if (loading) return <div className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

  return (
    <div className="space-y-6">
      <Card title="Email Notifications" description="Send every website form submission (Contact, Appointment, Careers) to an email inbox via SendGrid. All submissions are always saved in the Submissions tab too.">
        <div className="flex items-center gap-3 rounded-xl bg-brand-bluelight p-4">
          <Switch checked={form.enabled} onCheckedChange={(v) => up('enabled', v)} />
          <div>
            <Label className="text-slate-800 font-semibold">Email notifications {form.enabled ? 'ON' : 'OFF'}</Label>
            <p className="text-xs text-slate-500">When on, new submissions are emailed to the recipient below.</p>
          </div>
        </div>
      </Card>

      <Card title="SendGrid API" description="Paste your SendGrid API key (with Mail Send permission). It is stored securely and never shown again in full.">
        <div className="space-y-1.5">
          <Label className="text-slate-700 text-sm font-semibold flex items-center gap-1"><KeyRound size={14} /> SendGrid API Key</Label>
          <Input type="password" value={form.apiKey} onChange={(e) => up('apiKey', e.target.value)}
            placeholder={hasApiKey ? `Saved (${apiKeyMasked}) — leave blank to keep` : 'SG.xxxxxxxx...'} />
          {hasApiKey && <p className="text-xs text-brand-green flex items-center gap-1"><ShieldCheck size={13} /> A key is saved. Enter a new one only to replace it.</p>}
        </div>
      </Card>

      <Card title="Addresses">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-slate-700 text-sm font-semibold">From Email (verified sender)</Label>
            <Input value={form.fromEmail} onChange={(e) => up('fromEmail', e.target.value)} placeholder="website@comfortair.co" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-700 text-sm font-semibold">From Name</Label>
            <Input value={form.fromName} onChange={(e) => up('fromName', e.target.value)} placeholder="Comfort Air Website" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700 text-sm font-semibold flex items-center gap-1"><Mail size={14} /> Send Notifications To</Label>
          <Input value={form.toEmail} onChange={(e) => up('toEmail', e.target.value)} placeholder="ndm@comfortair.co" />
        </div>
        <p className="text-xs text-slate-500">Note: The "From Email" must be a verified sender or authenticated domain in your SendGrid account, or delivery will fail.</p>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button onClick={save} disabled={saving} className="bg-brand-green hover:bg-brand-greendark gap-2">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Settings
        </Button>
        <Button onClick={sendTest} disabled={testing} variant="outline" className="gap-2 border-brand-blue/30 text-brand-blue">
          {testing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send Test Email
        </Button>
      </div>
    </div>
  );
};

export default EmailSettings;
